import React, { useState } from 'react';
import {
  ClosePanelButton,
  formattedDollarValue,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalSize,
  PercentageSpan,
  TitledNumberInput,
  useTaxRate,
} from 'venn-components';
import { Icon } from 'venn-ui-kit';
import { Tooltip, TooltipContentWithArrow, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { analysisSubjectQuery, type SubjectWithOptionalFeeAndFilters, useCachedLoadableValue } from 'venn-state';
import { useWorkspaceTaxSettings } from 'venn-utils';
import { CapitalLossDeductionCapInput } from './tax/CapitalLossDeductionCapInput';

export interface EditTaxRatesModalProps {
  subject: SubjectWithOptionalFeeAndFilters;
  onClose: () => void;
}

export const EditTaxRatesModal = ({ subject, onClose }: EditTaxRatesModalProps) => {
  const subjectAnalysisSubjectQuery = analysisSubjectQuery(subject);
  const analysisSubject = useCachedLoadableValue(subjectAnalysisSubjectQuery, undefined);
  const portfolio = analysisSubject!.portfolio;
  const fund = analysisSubject!.fund;
  const isSubjectAnInvestment = !portfolio && analysisSubject!.fund?.id;
  const [taxRate, setTaxRate] = useTaxRate(subject);
  const {
    fedLtRate: baseLongTermRate,
    fedStRate: baseShortTermRate,
    niitRate: baseNiitRate,
    stateRate: baseStateRate,
    lossCap: baseLossCap,
  } = taxRate;
  const { results: workspaceDefaults } = useWorkspaceTaxSettings();
  const [longTermRate, setLongTermRate] = useState(baseLongTermRate);
  const [shortTermRate, setShortTermRate] = useState(baseShortTermRate);
  const [niitRate, setNiitRate] = useState(baseNiitRate);
  const [stateRate, setStateRate] = useState(baseStateRate);
  const [lossCap, setLossCap] = useState(baseLossCap);

  const hasChanges =
    longTermRate !== baseLongTermRate ||
    shortTermRate !== baseShortTermRate ||
    lossCap !== baseLossCap ||
    niitRate !== baseNiitRate ||
    stateRate !== baseStateRate;

  return (
    <Modal className="h-[535px] flex flex-col" onClose={onClose} size={ModalSize.Small} closeOnEsc noPadding>
      <ModalHeader className="bg-venn-grey-10 flex flex-row justify-between items-start m-0 py-[11px] px-[16px]">
        <span className="text-venn-grey-80 font-bold text-base !text-[14px] ">EDIT TAX RATES</span>
        <ClosePanelButton onClose={onClose} data-testid="qa-close-upload-panel" />
      </ModalHeader>

      <ModalContent
        className="border-t border-b overflow-y-scroll border-venn-grey-30 pt-[9px] !px-[16px] flex-1"
        style={{ borderTopStyle: 'solid', borderBottomStyle: 'solid' }}
      >
        <div className="flex justify-between items-center mb-[16px]">
          <span className="text-[14px] pt-[6px]">
            <Icon
              className={isSubjectAnInvestment ? 'text-venn-investment-gold' : 'text-venn-deep-dark-blue'}
              type={isSubjectAnInvestment ? 'square' : 'folder'}
            />{' '}
            {isSubjectAnInvestment ? fund?.name : portfolio?.name}
          </span>
          <Tooltip>
            <TooltipTrigger>
              <Icon
                className="text-venn-grey-60 text-[12px]"
                prefix="far"
                type="info-circle"
                data-testid="qa-edit-tax-rate-tooltip-trigger-icon"
              />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContentWithArrow
                side="top"
                className="w-[300px] text-[12px] leading-normal"
                data-testid="qa-edit-tax-rate-tooltip-portal"
              >
                <p className="font-bold m-0">Workspace defaults</p>
                <TooltipTaxInfoRow label="Long-Term Capital Gains" taxRate={workspaceDefaults?.fedLtRate ?? 0} />
                <TooltipTaxInfoRow label="Short-Term Capital Gains" taxRate={workspaceDefaults?.fedStRate ?? 0} />
                <TooltipTaxInfoRow
                  label="Net Investment Income Tax Rate (NIIT)"
                  taxRate={workspaceDefaults?.niitRate ?? 0}
                />
                <TooltipTaxInfoRow label="State Tax Rate" taxRate={workspaceDefaults?.stateRate ?? 0} />
                <TooltipTaxInfoRow
                  label="Capital Loss Deduction Cap"
                  taxRate={workspaceDefaults?.lossCap ?? 0}
                  spanType="Dollar"
                />
              </TooltipContentWithArrow>
            </TooltipPortal>
          </Tooltip>
        </div>
        <div className="pl-[18px]">
          <div className="flex gap-[24px] justify-between mb-[16px]">
            <TaxRateInputField
              taxRate={longTermRate}
              defaultRate={workspaceDefaults?.fedLtRate ?? 0}
              title="Long-Term Capital Gains"
              className="flex-1"
              setValue={setLongTermRate}
              testId="qa-long-term-rate"
            />
            <TaxRateInputField
              taxRate={shortTermRate}
              defaultRate={workspaceDefaults?.fedStRate ?? 0}
              title="Short-Term Capital Gains"
              className="flex-1"
              setValue={setShortTermRate}
              testId="qa-short-term-rate"
            />
          </div>
          <TaxRateInputField
            taxRate={niitRate}
            defaultRate={workspaceDefaults?.niitRate ?? 0}
            title="Net Investment Income Tax Rate (NIIT)"
            isOptional
            className="mb-[16px]"
            setValue={setNiitRate}
            testId="qa-niit-rate"
          />
          <TaxRateInputField
            taxRate={stateRate}
            defaultRate={workspaceDefaults?.stateRate ?? 0}
            title="State Tax Rate"
            isOptional
            className="mb-[16px]"
            setValue={setStateRate}
            testId="qa-state-rate"
          />
          <CapitalLossDeductionCapInput
            taxRate={lossCap}
            defaultRate={workspaceDefaults?.lossCap ?? 0}
            title="Capital Loss Deduction Cap"
            setValue={setLossCap}
            testId="qa-loss-cap"
          />
        </div>
      </ModalContent>
      <ModalFooter
        cancelLabel="CANCEL"
        className="!bg-venn-white !border-t-0 mb-[6px] mt-[6px] px-[12px]"
        primaryLabel="APPLY"
        simplifiedButtonStyles
        onCancel={onClose}
        onPrimaryClick={() => {
          setTaxRate({
            fedLtRate: longTermRate,
            fedStRate: shortTermRate,
            niitRate,
            stateRate,
            lossCap,
          });
          onClose();
        }}
        primaryDisabled={!hasChanges}
      />
    </Modal>
  );
};

const TooltipTaxInfoRow = ({
  label,
  taxRate,
  spanType,
}: {
  label: string;
  taxRate: number;
  spanType?: 'Percent' | 'Dollar';
}) => (
  <div className="flex justify-between">
    <span>{label}:</span>
    {spanType === 'Dollar' && <span className="font-bold">{formattedDollarValue(String(taxRate))}</span>}
    {spanType !== 'Dollar' && <PercentageSpan value={taxRate} className="font-bold" />}
  </div>
);

const TaxRateInputField = ({
  taxRate,
  defaultRate,
  title,
  setValue,
  className,
  testId,
  isOptional = false,
}: {
  taxRate: number;
  defaultRate: number;
  title: string;
  setValue: (value: number) => void;
  className?: string;
  testId?: string;
  isOptional?: boolean;
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className={className}>
      <TitledNumberInput
        title={title}
        subtitle={isOptional ? '(optional)' : undefined}
        value={taxRate}
        setValue={setValue}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        testId={testId}
      />
      <span className="text-[12px] text-venn-grey-80">
        {isFocused ? (
          <>
            Workspace default: <PercentageSpan value={defaultRate} />
          </>
        ) : (
          '\u00A0'
        )}
      </span>
    </div>
  );
};
